<script lang="ts">
  /**
   * A countdown that reads as a timer, not an answer: a ring draining around a modest
   * "12s". `total` defaults to the largest value seen since mount (works for fast dev rooms).
   */
  let { secs, total = 0, size = '56px', hurry = false, caption = '' }: {
    secs: number;
    total?: number;
    /** Any CSS length; the number scales with it. */
    size?: string;
    hurry?: boolean;
    /** Optional line under the ring, e.g. "seconds left". */
    caption?: string;
  } = $props();

  const R = 44;
  const C = 2 * Math.PI * R;
  let seen = $state(0);
  $effect.pre(() => { if (secs > seen) seen = secs; });
  const full = $derived(Math.max(total, seen, 1));
  const frac = $derived(Math.min(1, Math.max(0, secs / full)));
  // Drain smoothly, but jump (no transition) when the value goes up, e.g. a stale value at mount.
  let prev = -1;
  let jump = $state(true);
  $effect.pre(() => { jump = secs > prev; prev = secs; });
</script>

<div class="timer" style="--size:{size}">
  <div class="ring" class:hurry class:jump role="timer" aria-label="{secs} seconds left">
    <svg viewBox="0 0 100 100" aria-hidden="true">
      <circle class="track" cx="50" cy="50" r={R} />
      <circle class="fill" cx="50" cy="50" r={R} stroke-dasharray={C} stroke-dashoffset={C * (1 - frac)} transform="rotate(-90 50 50)" />
    </svg>
    <span class="num" aria-hidden="true">{secs}<span class="unit">s</span></span>
  </div>
  {#if caption}<span class="caption">{caption}</span>{/if}
</div>

<style>
  .timer { display: inline-grid; justify-items: center; gap: calc(var(--size) * 0.06); flex: none; }
  .ring { position: relative; width: var(--size); height: var(--size); display: grid; place-items: center; }
  svg { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; }
  circle { fill: none; stroke-width: 7; }
  .track { stroke: rgba(255, 255, 255, 0.14); }
  .fill {
    stroke: var(--cyan); stroke-linecap: round; filter: drop-shadow(0 0 4px var(--cyan));
    transition: stroke-dashoffset 1s linear, stroke 0.3s;
  }
  .jump .fill { transition: none; }
  .hurry .fill { stroke: var(--pink); filter: drop-shadow(0 0 5px var(--pink)); }
  .num {
    position: relative; font-weight: 900; line-height: 1; font-variant-numeric: tabular-nums; color: #fff;
    font-size: calc(var(--size) * 0.34);
  }
  .unit { font-size: 0.5em; font-weight: 800; opacity: 0.75; margin-left: 0.06em; }
  .hurry .num { color: var(--pink); }
  .caption { font-size: calc(var(--size) * 0.11); font-weight: 700; opacity: 0.7; letter-spacing: 0.04em; text-transform: uppercase; }
  @media (prefers-reduced-motion: reduce) { .fill { transition: none; } }
</style>
